import { useCallback, useEffect, useMemo, useState } from "react";
import { useGetList, useListContext, useNotify } from "ra-core";
import { Link } from "react-router";
import {
  Building2,
  Check,
  ChevronDown,
  Download,
  Euro,
  LayoutGrid,
  List as ListIcon,
  Loader2,
  Plus,
  RefreshCw,
  Sparkles,
  TrendingUp,
} from "lucide-react";

import { getSupabaseClient } from "../providers/supabase/supabase";

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { KPIStrip, type KPIItem } from "../shared/KPIStrip";
import { PageModeToggle } from "../shared/PageModeToggle";
import { formatCents } from "../finance/treasuryFormat";
import type { PageMode } from "@/hooks/usePageMode";

// Sprint 8 — Header de /companies conforme ent-direction1.jsx + KPIStrip
// (ent-shared.jsx). 6 cellules + sous-titre informatif + toggle Liste/Grille.
//
// Sprint 10.x — Refonte KPI Option D :
// - Source: vue `companies_overview` (1 row par company, agrège heat IA +
//   manual temp + counts + deals + subs + tasks overdue + age).
// - Rangée 1 "Signal" (6 KPIs, basés sur max_heat_tier IA, plus has_overdue
//   + orphan) — remplace l'ancienne strip basée sur max_temperature manuelle.
// - Rangée 2 "Valeur" (3 KPIs: MRR, pipeline ouvert, nouvelles 7j) avec
//   toggle 3 formats persisté localStorage pour le MRR.

export type CompanyViewMode = "list" | "grid";

// Heat tier IA — cohérent avec edge function compute-heat-score et la vue
// companies_overview (81+ brulant, 61+ chaud, 41+ tiede, 21+ froid, else mort).
type HeatTier = "brulant" | "chaud" | "tiede" | "froid" | "mort";

interface CompanyOverviewRow {
  id: number;
  company_id: number;
  // Heat IA
  max_heat_tier: HeatTier | null;
  max_heat_score: number | null;
  // Compteurs
  contacts_total: number;
  contacts_actifs: number;
  nb_deals: number;
  nb_open_deals: number;
  nb_active_plans: number;
  // Tasks overdue (Théo)
  overdue_tasks_count: number;
  has_overdue_tasks: boolean;
  // Payment Stripe
  payment_state: string;
  is_paying_loose: boolean;
  is_paying_strict: boolean;
  // Business
  pipeline_open_eur: number;
  mrr_monthly_eur: number;
  age_days: number;
}

export type CompanyKpiFilter =
  | "brulant"
  | "chaud"
  | "tiede"
  | "froid_mort"
  | "orphan"
  | "overdue"
  | "paying"
  | "has_pipeline"
  | "recent_7d"
  | "all";

interface CompanyPageHeadProps {
  mode: PageMode;
  onToggleMode: () => void;
  activeKpiFilter: CompanyKpiFilter;
  onKpiFilterChange: (f: CompanyKpiFilter) => void;
  viewMode: CompanyViewMode;
  onViewModeChange: (m: CompanyViewMode) => void;
}

// === MRR format toggle — persisté en localStorage ===

export type MrrFormat = "raw" | "compact" | "yearly";
const MRR_STORAGE_KEY = "companies_mrr_format";

function readMrrFormat(): MrrFormat {
  if (typeof window === "undefined") return "raw";
  try {
    const v = localStorage.getItem(MRR_STORAGE_KEY);
    return v === "compact" || v === "yearly" ? v : "raw";
  } catch {
    return "raw";
  }
}

function useMrrFormat(): [MrrFormat, (f: MrrFormat) => void] {
  const [fmt, setFmt] = useState<MrrFormat>(() => readMrrFormat());
  useEffect(() => {
    try {
      localStorage.setItem(MRR_STORAGE_KEY, fmt);
    } catch {
      // localStorage indispo (Safari incognito strict) — fail silently
    }
  }, [fmt]);
  return [fmt, setFmt];
}

// === Number formatters ===

const formatEurRaw = (eur: number) => formatCents(eur * 100);

function formatEurCompact(n: number): string {
  if (!Number.isFinite(n)) return "—";
  const abs = Math.abs(n);
  if (abs >= 1_000_000) {
    const v = n / 1_000_000;
    // 1.2 M€ → "1,2 M€" ; 100 M€ → "100 M€" (entier au delà de 100).
    return `${v.toFixed(v >= 100 ? 0 : 1).replace(".", ",")} M€`;
  }
  if (abs >= 1_000) {
    const v = n / 1_000;
    return `${v.toFixed(v >= 100 ? 0 : 1).replace(".", ",")} k€`;
  }
  return `${Math.round(n)} €`;
}

function formatMrr(monthlyEur: number, fmt: MrrFormat): string {
  if (!Number.isFinite(monthlyEur)) return "—";
  if (fmt === "compact") return `${formatEurCompact(monthlyEur)}/mo`;
  if (fmt === "yearly") return `${formatEurRaw(monthlyEur * 12)}/an`;
  return `${formatEurRaw(monthlyEur)}/mois`;
}

function formatPipeline(eur: number): string {
  if (eur >= 1_000_000) return formatEurCompact(eur);
  return formatEurRaw(eur);
}

// === Segmented control mini (3 modes) — réutilise pattern shadcn-admin-kit ===

const MRR_FORMAT_LABELS: Record<MrrFormat, string> = {
  raw: "Brut",
  compact: "Compact",
  yearly: "Année",
};

function MrrFormatToggle({
  value,
  onChange,
}: {
  value: MrrFormat;
  onChange: (f: MrrFormat) => void;
}) {
  const options: MrrFormat[] = ["raw", "compact", "yearly"];
  return (
    <div
      className="inline-flex items-center rounded-md border border-border p-0.5 bg-muted/40 gap-0.5"
      role="tablist"
      aria-label="Format CA récurrent"
    >
      {options.map((opt) => {
        const isActive = value === opt;
        return (
          <button
            key={opt}
            type="button"
            role="tab"
            aria-selected={isActive}
            onClick={() => onChange(opt)}
            className={cn(
              "h-5 px-1.5 rounded-sm text-[10px] font-medium transition-colors leading-none",
              isActive
                ? "bg-card text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            {MRR_FORMAT_LABELS[opt]}
          </button>
        );
      })}
    </div>
  );
}

export function CompanyPageHead({
  mode,
  onToggleMode,
  activeKpiFilter,
  onKpiFilterChange,
  viewMode,
  onViewModeChange,
}: CompanyPageHeadProps) {
  const notify = useNotify();
  const { data, total, refetch } = useGetList<CompanyOverviewRow>(
    "companies_overview",
    {
      pagination: { page: 1, perPage: 500 },
    },
  );

  const [mrrFormat, setMrrFormat] = useMrrFormat();
  const [mrrRefreshing, setMrrRefreshing] = useState(false);

  const refreshMrr = useCallback(async () => {
    setMrrRefreshing(true);
    try {
      const supabase = getSupabaseClient();
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session) throw new Error("not_authenticated");
      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/stripe-backfill`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${session.access_token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ since_days: 90 }),
        },
      );
      if (!res.ok) {
        const body = await res.text().catch(() => "");
        throw new Error(`HTTP ${res.status}: ${body.slice(0, 200)}`);
      }
      const json = (await res.json()) as {
        ok?: boolean;
        configured?: boolean;
        enabled?: boolean;
        message?: string;
        counts?: Record<string, number>;
      };
      if (json.configured === false) {
        notify("Stripe non configuré dans les Intégrations.", {
          type: "warning",
        });
        return;
      }
      if (json.enabled === false) {
        notify("Stripe désactivé dans les Intégrations.", { type: "warning" });
        return;
      }
      await refetch();
      const subs = json.counts?.subscriptions;
      notify(
        subs != null
          ? `MRR rafraîchi (${subs} abonnement${subs > 1 ? "s" : ""} sync)`
          : "MRR rafraîchi",
        { type: "success" },
      );
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      notify(`Erreur refresh MRR : ${msg}`, { type: "error" });
    } finally {
      setMrrRefreshing(false);
    }
  }, [notify, refetch]);

  const counts = useMemo(() => {
    const list = data ?? [];
    return {
      brulant: list.filter((c) => c.max_heat_tier === "brulant").length,
      chaud: list.filter((c) => c.max_heat_tier === "chaud").length,
      tiede: list.filter((c) => c.max_heat_tier === "tiede").length,
      froid_mort: list.filter(
        (c) => c.max_heat_tier === "froid" || c.max_heat_tier === "mort",
      ).length,
      orphan: list.filter((c) => (c.contacts_total ?? 0) === 0).length,
      overdue: list.filter((c) => c.has_overdue_tasks === true).length,
      // Rangée Valeur
      mrr_monthly_eur: list.reduce(
        (acc, c) => acc + (Number(c.mrr_monthly_eur) || 0),
        0,
      ),
      pipeline_open_eur: list.reduce(
        (acc, c) => acc + (Number(c.pipeline_open_eur) || 0),
        0,
      ),
      open_deals_total: list.reduce(
        (acc, c) => acc + (Number(c.nb_open_deals) || 0),
        0,
      ),
      recent_7d: list.filter((c) => (c.age_days ?? 999) <= 7).length,
      has_pipeline: list.filter((c) => (c.pipeline_open_eur ?? 0) > 0).length,
      paying: list.filter((c) => c.is_paying_loose === true).length,
    };
  }, [data]);

  const totalCompanies = total ?? 0;
  const pct = (n: number) =>
    totalCompanies > 0 ? `${Math.round((n / totalCompanies) * 100)}%` : "—";

  const toggleFilter = (f: CompanyKpiFilter) => {
    onKpiFilterChange(activeKpiFilter === f ? "all" : f);
  };

  // === Rangée 1 — Signal ===
  const signalItems: KPIItem[] = [
    {
      key: "brulant",
      label: "🔥 Brûlant",
      value: counts.brulant,
      pct: pct(counts.brulant),
      dotClass: "bg-red-500",
      active: activeKpiFilter === "brulant",
      onClick: () => toggleFilter("brulant"),
    },
    {
      key: "chaud",
      label: "🌶️ Chaud",
      value: counts.chaud,
      pct: pct(counts.chaud),
      dotClass: "bg-orange-500",
      active: activeKpiFilter === "chaud",
      onClick: () => toggleFilter("chaud"),
    },
    {
      key: "tiede",
      label: "🟡 À chauffer",
      value: counts.tiede,
      pct: pct(counts.tiede),
      dotClass: "bg-amber-400",
      active: activeKpiFilter === "tiede",
      onClick: () => toggleFilter("tiede"),
    },
    {
      key: "froid_mort",
      label: "🧊 Froid",
      value: counts.froid_mort,
      pct: pct(counts.froid_mort),
      dotClass: "bg-blue-500",
      active: activeKpiFilter === "froid_mort",
      onClick: () => toggleFilter("froid_mort"),
    },
    {
      key: "orphan",
      label: "👥 Orphelins",
      value: counts.orphan,
      pct: "sans contact lié",
      dotClass: "bg-violet-500",
      active: activeKpiFilter === "orphan",
      onClick: () => toggleFilter("orphan"),
    },
    {
      key: "overdue",
      label: "⏰ Action en retard",
      value: counts.overdue,
      pct: "à relancer",
      dotClass: "bg-red-700",
      active: activeKpiFilter === "overdue",
      onClick: () => toggleFilter("overdue"),
    },
  ];

  // === Rangée 2 — Valeur ===
  const valueItems: KPIItem[] = [
    {
      key: "mrr",
      label: "CA récurrent",
      value: formatMrr(counts.mrr_monthly_eur, mrrFormat),
      pct:
        counts.paying > 0
          ? `${counts.paying} compte${counts.paying > 1 ? "s" : ""} payant${counts.paying > 1 ? "s" : ""}`
          : "aucun compte payant",
      icon: <Euro />,
      iconClass: "text-emerald-600 dark:text-emerald-300",
      trailing: (
        <div className="inline-flex items-center gap-1.5">
          <MrrFormatToggle value={mrrFormat} onChange={setMrrFormat} />
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              void refreshMrr();
            }}
            disabled={mrrRefreshing}
            title="Resynchroniser depuis Stripe (admin)"
            aria-label="Rafraîchir le MRR"
            className="inline-flex items-center justify-center h-5 w-5 rounded-sm text-muted-foreground hover:text-foreground hover:bg-muted/60 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {mrrRefreshing ? (
              <Loader2 className="size-3 animate-spin" />
            ) : (
              <RefreshCw className="size-3" />
            )}
          </button>
        </div>
      ),
      active: activeKpiFilter === "paying",
      onClick: () => toggleFilter("paying"),
    },
    {
      key: "pipeline",
      label: "Pipeline ouvert",
      value: formatPipeline(counts.pipeline_open_eur),
      pct: `${counts.open_deals_total} deal${counts.open_deals_total > 1 ? "s" : ""} ouvert${counts.open_deals_total > 1 ? "s" : ""}`,
      icon: <TrendingUp />,
      iconClass: "text-violet-600 dark:text-violet-300",
      active: activeKpiFilter === "has_pipeline",
      onClick: () => toggleFilter("has_pipeline"),
    },
    {
      key: "recent_7d",
      label: "Nouvelles 7j",
      value: counts.recent_7d,
      pct: "cette semaine",
      icon: <Sparkles />,
      iconClass: "text-amber-500 dark:text-amber-300",
      active: activeKpiFilter === "recent_7d",
      onClick: () => toggleFilter("recent_7d"),
    },
  ];

  return (
    <div className="flex flex-col gap-3">
      {/* Title row */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3 min-w-0">
          <Building2 className="size-5 text-violet-700 dark:text-violet-300 shrink-0 mt-0.5" />
          <div className="min-w-0">
            <h1 className="text-[20px] font-semibold tracking-tight truncate leading-tight">
              Entreprises
            </h1>
            <p className="text-[12.5px] text-muted-foreground mt-0.5">
              {totalCompanies} entreprise{totalCompanies > 1 ? "s" : ""}
              {counts.brulant > 0 && (
                <>
                  {" · "}
                  <span className="text-red-600 dark:text-red-300 font-medium">
                    {counts.brulant} brûlante{counts.brulant > 1 ? "s" : ""}
                  </span>
                </>
              )}
              {counts.orphan > 0 && (
                <>
                  {" · "}
                  <span className="text-violet-600 dark:text-violet-300 font-medium">
                    {counts.orphan} compte{counts.orphan > 1 ? "s" : ""}{" "}
                    orphelin{counts.orphan > 1 ? "s" : ""} à enrichir
                  </span>
                </>
              )}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {/* Segmented Liste/Grille */}
          <div className="inline-flex items-center rounded-md border border-border p-0.5 bg-card">
            <button
              type="button"
              onClick={() => onViewModeChange("list")}
              className={cn(
                "inline-flex items-center gap-1.5 h-7 px-2.5 rounded text-[12px] font-medium transition-colors",
                viewMode === "list"
                  ? "bg-muted text-foreground"
                  : "text-muted-foreground hover:text-foreground",
              )}
              aria-pressed={viewMode === "list"}
            >
              <ListIcon className="size-3.5" />
              Liste
            </button>
            <button
              type="button"
              onClick={() => onViewModeChange("grid")}
              className={cn(
                "inline-flex items-center gap-1.5 h-7 px-2.5 rounded text-[12px] font-medium transition-colors",
                viewMode === "grid"
                  ? "bg-muted text-foreground"
                  : "text-muted-foreground hover:text-foreground",
              )}
              aria-pressed={viewMode === "grid"}
            >
              <LayoutGrid className="size-3.5" />
              Grille
            </button>
          </div>

          <SortDropdown />

          <ExportButton />

          <Link
            to="/companies/create"
            className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-[12.5px] font-medium text-white"
            style={{ background: "var(--accent-solid)" }}
          >
            <Plus className="size-3.5" />
            Nouvelle entreprise
          </Link>

          <PageModeToggle mode={mode} onToggle={onToggleMode} />
        </div>
      </div>

      {/* Sprint 10.x — 2 strips séparées (signal puis valeur) pour clarté
          visuelle. Le wrap responsive est géré par KPIStrip lui-même. */}
      <KPIStrip items={signalItems} />
      <KPIStrip items={valueItems} />
    </div>
  );
}

// === Trier dropdown — affiche la valeur de tri active ===

const SORT_OPTIONS: { value: string; order: "ASC" | "DESC"; label: string }[] =
  [
    { value: "name", order: "ASC", label: "Nom (A → Z)" },
    { value: "name", order: "DESC", label: "Nom (Z → A)" },
    { value: "created_at", order: "DESC", label: "Plus récentes d'abord" },
    { value: "created_at", order: "ASC", label: "Plus anciennes d'abord" },
    { value: "nb_contacts", order: "DESC", label: "Plus de contacts d'abord" },
  ];

function SortDropdown() {
  const { sort, setSort } = useListContext();
  const activeLabel = (() => {
    if (!sort) return SORT_OPTIONS[0].label;
    const found = SORT_OPTIONS.find(
      (o) => o.value === sort.field && o.order === sort.order,
    );
    return found?.label ?? `${sort.field} (${sort.order})`;
  })();

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-md border border-border bg-card text-[12px] text-muted-foreground hover:bg-muted/40 hover:text-foreground shrink-0"
        >
          <span className="font-normal">Trier :</span>
          <span className="font-medium text-foreground truncate max-w-[180px]">
            {activeLabel}
          </span>
          <ChevronDown className="size-3 text-muted-foreground/70 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" sideOffset={4} className="p-1 w-[220px]">
        {SORT_OPTIONS.map((opt) => {
          const selected =
            sort?.field === opt.value && sort?.order === opt.order;
          return (
            <button
              key={`${opt.value}-${opt.order}`}
              type="button"
              onClick={() => setSort?.({ field: opt.value, order: opt.order })}
              className={cn(
                "w-full flex items-center gap-2 px-2 py-1.5 rounded-sm text-[12.5px] text-left hover:bg-muted/60",
                selected && "bg-muted/40",
              )}
            >
              <span className="flex-1">{opt.label}</span>
              {selected && (
                <Check className="size-3.5 text-emerald-600 dark:text-emerald-400" />
              )}
            </button>
          );
        })}
      </PopoverContent>
    </Popover>
  );
}

// === Bouton Exporter — réutilise getData de useListContext ===

function ExportButton() {
  const { getData, total } = useListContext();

  const handleExport = async () => {
    if (!getData) return;
    try {
      const data = await getData({ maxResults: 1000 });
      const headers = [
        "id",
        "name",
        "sector",
        "size",
        "city",
        "country",
        "website",
        "phone_number",
        "sales_id",
        "created_at",
      ];
      const rows = (data as Record<string, unknown>[]).map((r) =>
        headers
          .map((h) => {
            const v = r[h];
            if (v == null) return "";
            const s = String(v).replace(/"/g, '""');
            return s.includes(",") || s.includes("\n") ? `"${s}"` : s;
          })
          .join(","),
      );
      const csv = [headers.join(","), ...rows].join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `entreprises_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("export error", err);
    }
  };

  return (
    <button
      type="button"
      onClick={handleExport}
      disabled={!total}
      className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-md border border-border bg-card text-[12px] text-muted-foreground hover:bg-muted/40 hover:text-foreground shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
    >
      <Download className="size-3.5" />
      Exporter
    </button>
  );
}
